import { factory } from "@westpierstudio/luma";
import { Ease, Tween } from "@westpierstudio/luma/createjs";
import { AssetSpec, ParticleEmitterAssetSpec, SpineAssetSpec, SpriteAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { View } from "@westpierstudio/luma/mvc";
import { Resettable } from "@westpierstudio/luma/utils/reset";
import { GameServices } from "game/GameServices";
import { cloneDeep, random, set } from "lodash";
import { Emitter, LinkedListContainer } from "pixi-particles";
import { Spine } from "pixi-spine";
import { Container, Sprite, Text, Texture } from "pixi.js";
import { MultiplierData } from "servertypes/gameevents/FreeSpin";
import { getSoundPlayer } from "sound/SoundLocator";
import { Signal } from "type-signals";
import { devLog, generateTarget } from "utils/Misc";
import { CrossbowMultipliers } from "./CrossbowMultipliers";
import { EmitterOrbit } from "./EmitterOrbit";
import { ReelsLayout } from "./reels/ReelsModel";

class CrossbowBonusSignals {
    bonusComplete = new Signal();
}

interface CrossbowSettings {
    startAngle: number,
    bowLandDuration: number,
    bowSpinDuration: number,
    arrowDuration: number,
    bowFadeBadgeScale: number
    bowFadeDuration: number,
    delayAfterTargetHit: number,
    bowSpinTotalRotations: number,
    particleOrbitVelocity: number,
    bowSpinEaseInOutAmount: number,
    bowLandEaseOutAmount: number
}

interface CrossbowTargetSpec extends AssetSpec {
    skin: "left" | "right"
}

type Point = { x: number, y: number };

export interface CrossbowBonusSpec extends AssetSpec {
    settings: CrossbowSettings
    targets: CrossbowTargetSpec[],
    crossbowSpine: SpineAssetSpec
    crossbowParticles: ParticleEmitterAssetSpec
    crossBowOffset: AssetSpec
    arrowSprite: SpriteAssetSpec
    arrowStartOffset: Point
    badgeSprite: SpriteAssetSpec
    targetSpine: SpriteAssetSpec
    multiplierSpines: SpineAssetSpec[]
}

interface CrossbowCurrentData {
    validTargets: number[]
    multipliers: number[]
}

export class CrossbowBonus extends View implements Resettable {
    private readonly spec: CrossbowBonusSpec;
    private readonly settings: CrossbowSettings;
    private readonly targets: Spine[] = [];
    private bow!: Spine;
    private multipliers!: CrossbowMultipliers;
    private arrow!: Sprite;
    private badge!: Sprite;
    private particles!: EmitterOrbit;
    private tweenQueue?: createjs.Tween[];
    private isSkip = false;
    private skiptweens: createjs.Tween[] = [];
    signals = new CrossbowBonusSignals();

    get bowAngle() {
        return this.bow.rotation;
    }

    set bowAngle(value: number) {
        this.bow.angle = value;
        //Uncomment this to change skin while the bow is spinning
        //this.chooseSkinFromCurrent()
    }

    constructor(protected override readonly services: GameServices) {
        super(services);

        const { factory, resources } = this.services;

        this.spec = resources.getSpec(`crossbowbonus`);
        this.settings = this.spec.settings;

        this.setupTargets(this.spec);
        this.setupBow();

        this.visible = false;
    }

    setupMultipliers(multipliers: CrossbowMultipliers) {
        this.multipliers = multipliers;
    }

    /*
        There is no info about choice of symbol pos, or when to use column 3 in either the BE data
        or the content key, so doing the following for now:

        use 2 for 1st targeting the right,
        use 4 for 1st targeting the left
        use 3 if necessary for the final shot in either direction

        pick a rnd row
    */
    startRevealMultipliers(data: MultiplierData, reelsLayout: ReelsLayout): void {

        const { projectiles, multipliers, totalMultiplier } = data;
        const validTargets = projectiles.filter((v) => v > 0);
        const increments = this.calculateIncrements(validTargets);
        const settings = this.settings;

        this.resetTargetMultipliers();
        this.tweenQueue = [];
        let tween = Tween.get(this.bow, { paused: false });
        this.tweenQueue.push(tween);
        this.isSkip = false;

        let col: number, row: number;
        const usedColumns: number[] = [];

        for (const [i, target] of validTargets.entries()) {
            const targetObj = this.targets[target - 1];
            const isLast = i === (validTargets.length - 1);
            tween.call(() => {

                if (target <= 4) {
                    col = usedColumns.includes(3) ? 2 : 3;
                } else {
                    col = usedColumns.includes(1) ? 2 : 1;
                }

                usedColumns.push(col);

                row = random(0, reelsLayout.symbolCount - 1);

                const pos = reelsLayout.symbolPositions[col][row];

                this.badge.x = pos.x + (this.spec.crossBowOffset.x ?? 0);
                this.badge.y = pos.y + (this.spec.crossBowOffset.y ?? 0);

                this.resetBowAnim();

                this.badge.visible = true;
                this.badge.alpha = 1;
                this.bow.visible = true;
                this.bow.alpha = 1;
                this.bow.angle = settings.startAngle;
                this.bowLand(settings.bowLandDuration);
                getSoundPlayer().crossbowLand(i);
            })
                .wait(settings.bowLandDuration)
                .call(() => {
                    this.bowSpin(targetObj, settings.bowSpinDuration);
                })
                .wait(settings.bowSpinDuration);

            //create new tween to continue after target hit
            const nextTween = Tween.get(this.bow, { paused: true });
            this.tweenQueue.push(nextTween);
            tween.call(() => {
                //shoot arrow
                const createArrowTween = () => this.moveArrow(targetObj, settings.arrowDuration);
                const arrowTweenOnComplete = () => {
                    this.targetHitMultiplier(targetObj, multipliers[i], increments[i]);
                    nextTween.paused = false;
                    this.tweenQueue?.shift();
                    getSoundPlayer().crossbowTargetHit();
                    if (!isLast) getSoundPlayer().crossbowTimes(multipliers[i]);
                    else getSoundPlayer().crossbowTimesMultiplier(totalMultiplier);
                };
                this.bow.state.setAnimation(0, `fire`, false);
                this.bow.state.addListener({
                    event: function (entry, event) {
                        if (event.data.name === `arrow-fire`) {
                            const arrowTween = createArrowTween();
                            arrowTween.call(() => arrowTweenOnComplete());
                        }
                    }
                    // @ts-ignore
                }, this);
                //request animation frame to allow all tweens to finishing skipping
                requestAnimationFrame(() => {
                    this.badge.scale.set(1, 1);
                    this.bow.visible = true;
                    this.bow.alpha = 1;
                    this.badge.visible = true;
                    this.badge.alpha = 1;
                });
            });
            //swap to a new tween to allow skipping (skipping a tween will move here)
            tween = nextTween;

            tween.call(() => {
                this.skiptweens = [];
                this.isSkip = false;
            })
                .wait(settings.delayAfterTargetHit)
                .call(() => {
                    getSoundPlayer().crossbowIncrement(increments, i);
                    this.bowFade(settings.bowFadeDuration);
                    if (isLast) this.multiplierCompleteAnim();
                })
                .wait(settings.bowFadeDuration + 250)
                .call(() => {
                    if (isLast) {
                        tween.call(() => this.badge.visible = false);
                        this.tweenQueue = undefined;
                        this.signals.bonusComplete.dispatch();
                    }
                });
        }
    }

    private setupTargets(spec: CrossbowBonusSpec): void {

        const { targets } = spec;
        const { layout, factory } = this.services;

        let spine: Spine;

        for (const item of targets) {

            spine = generateTarget(this.services, spec);
            spine.skeleton.setSkinByName(item.skin);
            spine.x = item.x ?? 0;
            spine.y = item.y ?? 0;
            this.addChild(spine);
            this.targets.push(spine);
        }
    }

    private setupBow(): void {
        const { layout, factory } = this.services;

        this.particles = this.createCustomEmitter(this.spec.crossbowParticles, this);
        this.badge = factory.createSprite(this.spec.badgeSprite, this);
        this.bow = factory.createSpine(this.spec.crossbowSpine, this.badge);
        this.bow.skeleton.setSkinByName(`up`);
        this.arrow = factory.createSprite(this.spec.arrowSprite, this);
        this.particles.emit = false;
        this.arrow.visible = false;
        this.badge.visible = false;
    }

    private createCustomEmitter(spec: ParticleEmitterAssetSpec, parent?: Container, index?: number) {
        const container = new LinkedListContainer();
        const texture = (spec.particle as { textureKeys: string[] }).textureKeys.map((texture: string) => this.services.resources.getTexture(texture));
        const emitter = new EmitterOrbit(
            container,
            texture,
            spec.emitter
        );

        emitter.autoUpdate = true;
        this.services.factory.applySpecAndParent(container as unknown as Container, spec, parent, index);
        return emitter;
    }

    private moveArrow(targetObj: Spine, duration: number): createjs.Tween {
        this.arrow.position = this.badge.position;
        const current = this.arrow.position;
        const targetPos = targetObj.position;
        this.arrow.angle = this.angleToTarget(targetPos, current);
        this.arrow.x += this.spec.arrowStartOffset.x * Math.cos(this.arrow.rotation) + this.spec.arrowStartOffset.y * Math.sin(this.arrow.rotation);
        this.arrow.y += this.spec.arrowStartOffset.x * Math.sin(this.arrow.rotation) + this.spec.arrowStartOffset.y * Math.cos(this.arrow.rotation);
        this.arrow.visible = true;
        const tween = Tween.get(this.arrow)
            .to({ x: targetPos.x, y: targetPos.y }, duration, Ease.linear)
            .call(() => {
                this.arrow.visible = false;
                this.targetHitAnim(targetObj);
            });
        return tween;
    }

    /**
     * multiplier cannot be incremented more than once
     * @param hitTargets from server
     * @returns array of number, -1 means no increment
     */
    private calculateIncrements(hitTargets: number[]): number[] {
        const increment: number[] = [];
        for (let i = 0; i < hitTargets.length; i++) {
            const targetHit = hitTargets[i];
            let prev = -1;
            for (let j = 0; j < i; j++) {
                const prevTarget = hitTargets[j];
                if (prevTarget === targetHit) prev = j;
            }
            increment.push(prev);
        }
        return increment;
    }

    private bowLand(duration: number) {
        if (this.isSkip) {
            this.badge.scale.set(1, 1);
            this.bow.visible = true;
            this.bow.alpha = 1;
            this.badge.visible = true;
            this.badge.alpha = 1;
        }
        else {
            this.particles.cleanup();
            this.particles.orbitVelocity = 0;
            this.particles.parent.rotation = 0;
            this.particles.parent.position = this.badge.position;
            this.particles.emit = true;
            this.badge.scale.set(0, 0);
            const t1 = Tween.get(this.badge.scale)
                .to({ x: 0, y: 0 }, 0)
                .to({ x: 1, y: 1 }, duration, Ease.getBackOut(this.settings.bowLandEaseOutAmount))
                .call(() => {
                    this.badge.scale.set(1, 1);
                    this.bow.visible = true;
                    this.bow.alpha = 1;
                    this.badge.visible = true;
                    this.badge.alpha = 1;
                });
            this.skiptweens.push(t1);
        }
    }

    private bowSpin(container: Container, duration: number) {
        const { bowSpinTotalRotations, startAngle, particleOrbitVelocity, bowSpinEaseInOutAmount } = this.settings;
        const angleToTarget = this.angleToTarget(this.badge.position, container.position) - 90 + (360 * bowSpinTotalRotations);
        if (this.isSkip) {
            this.bowAngle = angleToTarget;
        }
        else {
            getSoundPlayer().crossbowSpin();
            const t1 = Tween.get(this)
                .to({ bowAngle: startAngle }, 0)
                .to({ bowAngle: angleToTarget }, duration, Ease.getBackInOut(bowSpinEaseInOutAmount))
                .call(() => this.bowAngle = angleToTarget);
            const t2 = Tween.get(this.bow)
                .wait(duration / 2)
                .call(() => this.chooseSkinFromAngle(angleToTarget));
            const t3 = Tween.get(this.particles)
                .to({ orbitVelocity: particleOrbitVelocity }, duration / 2, Ease.quadIn)
                .call(() => {
                    this.particles.emit = false;
                    this.particles.orbitVelocity = particleOrbitVelocity;
                });
            this.skiptweens.push(t1, t2, t3);
        }
    }

    private bowFade(duration: number) {
        if (this.isSkip) return;
        const { bowFadeBadgeScale } = this.settings;
        const t1 = Tween.get(this.badge.scale)
            .to({ x: 1, y: 1 }, 0)
            .to({ x: bowFadeBadgeScale, y: bowFadeBadgeScale }, duration)
            .call(() => this.badge.scale.set(bowFadeBadgeScale, bowFadeBadgeScale));
        const t2 = Tween.get(this.badge)
            .wait(50)
            .to({ alpha: 1 }, 0)
            .to({ alpha: 0 }, duration)
            .call(() => {
                this.badge.alpha = 1;
                this.badge.visible = false;
            });
        const t3 = Tween.get(this.bow)
            .to({ alpha: 1 }, 0)
            .to({ alpha: 0 }, duration)
            .call(() => {
                this.bow.alpha = 1;
                this.bow.visible = false;
            });
        this.skiptweens.push(t1, t2, t3);
    }

    private angleToTarget(target: Point, current: Point): number {
        return Math.atan2(target.y - current.y, target.x - current.x) * 180 / Math.PI;
    }

    private resetBowAnim() {
        this.bow.skeleton.setSkinByName(`up`);
        this.bow.state.clearTrack(0);
        this.bow.skeleton.setToSetupPose();
        this.bow.state.clearListeners();
    }

    private chooseSkinFromAngle(angleToTarget: number): void {
        angleToTarget = angleToTarget % 360;
        if (angleToTarget > 90 && angleToTarget < 270) {
            this.bow.skeleton.setSkinByName(`down`);
        }
        else {
            this.bow.skeleton.setSkinByName(`up`);
        }
    }

    private chooseSkinFromCurrent() {
        this.chooseSkinFromAngle(this.bow.angle);
    }

    private targetHitMultiplier(target: Spine, multiplier: number, increment: number) {
        if (increment >= 0) {
            const active = this.multipliers.activeSpines[increment];
            const multiplierSpine = active[1].reference;
            const replacement = this.multipliers.get(active[0] + 1).reference;
            replacement.visible = false;
            multiplierSpine.state.setAnimation(0, `Increment`, false);
            multiplierSpine.state.addListener({
                complete: () => {
                    replacement.position = target.position;
                    replacement.visible = true;
                    multiplierSpine.visible = false;
                    multiplierSpine.state.clearListeners();
                }
            });
        }
        else {
            const multiplierSpine = this.multipliers.get(multiplier)?.reference;
            if (multiplierSpine) {
                multiplierSpine.position = target.position;
                multiplierSpine.state.setAnimation(0, `In`, false);
            }
        }
    }

    private multiplierCompleteAnim() {
        for (const [val, spine] of this.multipliers.activeSpines) {
            spine.reference.state.setAnimation(0, `Loop`, true);
        }
    }

    private targetHitAnim(target: Spine) {
        target.state.setAnimation(0, `hit`, false);
    }

    resetTargetMultipliers() {
        this.multipliers.resetMultipliers();
    }

    override reset(): void {
        this.badge.visible = false;
        this.arrow.visible = false;
        this.resetTargetMultipliers();
        this.particles.emit = false;
        this.particles.cleanup();
    }

    skip() {
        if (this.tweenQueue && this.tweenQueue.length > 1) {
            this.isSkip = true;
            this.tweenQueue[0].timeScale = Infinity;
            this.skipSubTweens();
            this.particles.emit = false;
            this.particles.cleanup();
            getSoundPlayer().stopCrossbowSounds();
        }
        else if (this.tweenQueue === undefined) {
            this.signals.bonusComplete.dispatch();
        }
    }

    private skipSubTweens() {
        for (const tween of this.skiptweens) {
            tween.timeScale = Infinity;
        }
    }
}

